import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Sparkles, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";

const AdminPromptLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai-prompt-logs?limit=100`, {
        });
      setLogs(res.data?.logs || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div className="space-y-6" data-testid="admin-prompt-logs">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#002FA7]" /> AI Prompt Logs
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Most recent prompts the system sent to ChatGPT during roster generation. Useful for troubleshooting AI output quality.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white px-3 py-2 border border-zinc-800 rounded"
          data-testid="prompt-logs-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 p-12 text-center text-sm text-zinc-500" data-testid="prompt-logs-empty">
          No AI prompts have been logged yet. Generate a roster with custom rules or AI instructions to see prompts here.
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 divide-y divide-zinc-800">
          {logs.map((log) => {
            const open = !!expanded[log.log_id];
            return (
              <div key={log.log_id} className="p-4" data-testid={`log-${log.log_id}`}>
                <button
                  onClick={() => toggle(log.log_id)}
                  className="w-full flex items-start gap-3 text-left"
                >
                  {open ? (
                    <ChevronDown className="w-4 h-4 mt-1 text-zinc-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 mt-1 text-zinc-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-white">
                        {log.purpose || "ai_polish"}
                      </span>
                      <span className="text-[10px] uppercase tracking-wide bg-zinc-800 text-zinc-300 px-1.5 py-0.5 rounded">
                        {log.model || "openai"}
                      </span>
                      {log.user_email && (
                        <span className="text-xs text-zinc-400">
                          {log.user_email}
                        </span>
                      )}
                      {log.roster_name && (
                        <span className="text-xs text-zinc-400">
                          · {log.roster_name}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 tabular-nums">
                      {log.created_at}
                    </p>
                  </div>
                </button>
                {open && (
                  <div className="mt-4 ml-7 space-y-3">
                    {log.system_message && (
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">System</p>
                        <pre className="text-xs text-zinc-300 bg-black/40 border border-zinc-800 rounded p-3 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                          {log.system_message}
                        </pre>
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Prompt</p>
                      <pre className="text-xs text-zinc-300 bg-black/40 border border-zinc-800 rounded p-3 whitespace-pre-wrap break-words max-h-96 overflow-y-auto" data-testid={`log-prompt-${log.log_id}`}>
                        {log.prompt}
                      </pre>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Response</p>
                      <pre className="text-xs text-emerald-300 bg-black/40 border border-zinc-800 rounded p-3 whitespace-pre-wrap break-words max-h-96 overflow-y-auto" data-testid={`log-response-${log.log_id}`}>
                        {log.response}
                      </pre>
                    </div>
                    {log.ai_instructions && (
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Admin Instructions</p>
                        <pre className="text-xs text-zinc-300 bg-black/40 border border-zinc-800 rounded p-3 whitespace-pre-wrap break-words">
                          {log.ai_instructions}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminPromptLogs;
